import { Component, ElementRef, OnDestroy, Renderer2, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { LoadingService } from "@core/loading.service";
import { User } from "@interfaces/user.i";
import { IonicModule, ModalController } from "@ionic/angular";
import { defaultIfEmpty, firstValueFrom, map, retry, Subscription, take, timeout } from "rxjs";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { CommonVariable } from "src/app/app.constants";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { PaymentDataService } from "src/app/services/payment-data.service";
import { PaymentService } from "src/app/services/payment.service";
import { TokenService } from "src/app/services/token.service";
import { SafeHtmlPipe } from "../../../pipe/safehtmlpipe";
import { WalletService } from "../../account/account-wallet/wallet.services";
import { NoThanksPopupComponent } from "../no-thanks-popup/no-thanks-popup.component";
import { scheduleSuccessBannerHideTimer } from "../utils/success-banner.util";
import { BasePaymentService } from "../payment/services/base-payment-service";
import { PaymentNavigationService } from "../payment/services/payment-navigation-service";
import { StoreRepositoryService } from "../../store/store-repository.service";

interface PendingOptionalPurchase {
  userId: string;
  prosubId: string;
  customerPurchaseId: string;
  receiptId: string;
}

@Component({
    selector: "app-free-reg-step4b",
    templateUrl: "./free-reg-step4b.component.html",
    styleUrl: "./free-reg-step4b.component.scss",
    standalone: true,
    imports: [IonicModule, SafeHtmlPipe],
    providers: [BasePaymentService, PaymentNavigationService],
})
export class FreeRegStep4bComponent implements OnDestroy {
  @ViewChild("dynamicContent") dynamicContent: ElementRef;
  paymentPageData: string;
  private prosubId: string;
  public loaded = false;
  private addOnProsubId: string;
  private offerId: string;
  private claimDestination: Promise<string | null> = Promise.resolve(null);
  private claimNavigationInProgress = false;
  private claimNavigationEpoch = 0;
  public automaticBaseClaimPending = false;
  private automaticBaseClaimTimer: ReturnType<typeof setTimeout> | null = null;
  private automaticBaseClaimCompleted: object | null = null;
  private automaticBaseClaimAttempts = 0;
  private automaticBaseClaimProfileSubscription?: Subscription;
  private automaticBaseClaimInProgress = false;
  private optionalPurchaseInProgress = false;
  private optionalPurchaseConfirmedContext: object | null = null;
  private pendingOptionalPurchase: PendingOptionalPurchase | null = null;
  private optionalPurchaseStatusRead?: Subscription;
  private optionalPurchaseStatusTimer?: ReturnType<typeof setTimeout>;
  private optionalPurchaseAuthUnsubscribe?: () => void;
  private optionalPurchaseStatusAttempts = 0;
  private optionalPurchaseRequestInFlight = false;
  private viewActive = false;
  upsellID: string;
  private tp1: string;
  private tp2: string;
  private tp3: string;
  private tID: string;
  private purchaseDetail: any;
  private altProsubDetails: any;
  private purchaseID: string;
  private syndicateId: string;
  private iframeSmsJourneyDoc = "";
  private successBannerHideTimer: ReturnType<typeof setTimeout> | null = null;
  private successBannerScheduleTimer: ReturnType<typeof setTimeout> | null = null;
  private isDestroyed = false;
  private processcustomer_offer: string;
  /** Same gate as free-reg 4c payment page (`payment.page.ts`). */
  isCappingCheckDone = false;
  /** True when check4cFlow reports offer capping_status / cappingStatus. */
  private isOfferCapped = false;
  private userId: string;

  constructor(
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loadingService: LoadingService,
    private authPartialRegService: AuthPartialRegService,
    private paymentService: PaymentService,
    private walletService: WalletService,
    private matDialog: MatDialog,
    private gtmSegment: GTMSegmentService,
    private modalController: ModalController,
    private paymentDataService: PaymentDataService,
    private renderer: Renderer2,
    private tokenService: TokenService,
    private paymentNavigationService: PaymentNavigationService,
    private storeService: StoreRepositoryService
  ) { }

  ionViewWillEnter() {
    try {
      ++this.claimNavigationEpoch;
      this.viewActive = true;
      this.claimNavigationInProgress = false;
      this.claimDestination = Promise.resolve(null);
      this.loadingService.present();
      this.isCappingCheckDone = false;
      this.isOfferCapped = false;
      const queryParams = this.activatedRoute.snapshot.queryParams
      this.prosubId = queryParams?.Prosub_ID ? queryParams?.Prosub_ID : queryParams?.prosub_id;
      this.upsellID = queryParams?.upsell_ID;
      this.tp1 = queryParams?.TP1;
      this.tp2 = queryParams?.TP2;
      this.tp3 = queryParams?.TP3;
      this.tID = queryParams?.T_ID;
      this.purchaseID = queryParams?.purchase_ID;
      this.userId = this.tokenService.getUserId();

      localStorage.setItem("prosub_id", this.prosubId);
      this.optionalPurchaseAuthUnsubscribe?.();
      this.optionalPurchaseAuthUnsubscribe = onAuthStateChanged(getAuth(), () => {
        this.suspendOptionalPurchaseStatus();
        this.resumeOptionalPurchaseStatus();
      });
      this.resumeOptionalPurchaseStatus();

      if (this.prosubId) {
        const viewEpoch = this.claimNavigationEpoch;
        this.authPartialRegService.partial_Reg(this.prosubId, "", "").subscribe((paymentInfo) => {
          if (this.isDestroyed || viewEpoch !== this.claimNavigationEpoch) return;
          this.altProsubDetails = paymentInfo?.prosubDetail;
          this.iframeSmsJourneyDoc = this.altProsubDetails?.iframe_sms_journey || "";
          this.purchaseDetail = this.paymentService?.formPaymentDataForTopUp(this.altProsubDetails);
          this.paymentPageData = paymentInfo.paymentHtml;
          this.paymentPageData = this.replacePlaceholderWithExpiry(this.paymentPageData);
          const addonId = paymentInfo?.prosubDetail?.addon_prosub_id;
          this.addOnProsubId = addonId == null ? "" : String(addonId).trim();
          this.offerId = paymentInfo?.prosubDetail?.offer_id;
          this.claimDestination = this.resolveClaimDestination(this.addOnProsubId);

          // Same capping validation entry-point as free-reg 4c payment page
          this.authPartialRegService.check4cFlow(this.prosubId).subscribe(
            (r) => {
              if (this.isDestroyed || viewEpoch !== this.claimNavigationEpoch) return;
              if (!r) {
                this.authService.errorHandlerLogs("check4cFlow returned null in free-reg-step4b");
                this.isCappingCheckDone = true;
                this.finishPageLoad();
                return;
              }

              this.afterFourCData(r);

              // Same isCappingCheckDone assignment as free-reg 4c payment.page,
              // plus keep claim blocked when offer capping_status/cappingStatus is true
              const isOfferCapped =
                r?.offerCapping?.cappingStatus === true ||
                r?.offerCapping?.capping_status === true;
              if (
                (r?.purchaseCapping?.paymentCappingSyndicateId && !r?.purchaseCapping?.goodForPayment) ||
                isOfferCapped
              ) {
                this.isCappingCheckDone = false;
              } else {
                this.isCappingCheckDone = true;
              }

              this.finishPageLoad();
            },
            (err) => {
              if (this.isDestroyed || viewEpoch !== this.claimNavigationEpoch) return;
              this.authService.errorHandlerLogs("check4cFlow error in free-reg-step4b:", err);
              this.isCappingCheckDone = true;
              this.finishPageLoad();
              return err;
            }
          );
        });
      }

    } catch {
      this.loadingService.dismiss();
    }
  }

  ionViewWillLeave(): void {
    this.viewActive = false;
    this.optionalPurchaseAuthUnsubscribe?.();
    this.optionalPurchaseAuthUnsubscribe = undefined;
    this.suspendOptionalPurchaseStatus();
    if (this.automaticBaseClaimTimer != null) clearTimeout(this.automaticBaseClaimTimer);
    this.automaticBaseClaimTimer = null;
    this.automaticBaseClaimProfileSubscription?.unsubscribe();
    this.automaticBaseClaimProfileSubscription = undefined;
    this.automaticBaseClaimInProgress = false;
    void this.paymentNavigationService.clearPreparation?.();
    if (this.claimNavigationInProgress) this.loadingService.dismiss();
    ++this.claimNavigationEpoch;
    this.claimNavigationInProgress = false;
  }

  ngOnDestroy(): void {
    this.ionViewWillLeave();
    this.isDestroyed = true;
    if (this.successBannerScheduleTimer) {
      clearTimeout(this.successBannerScheduleTimer);
      this.successBannerScheduleTimer = null;
    }
    if (this.successBannerHideTimer) {
      clearTimeout(this.successBannerHideTimer);
      this.successBannerHideTimer = null;
    }
  }

  private finishPageLoad(): void {
    this.loaded = true;
    this.loadingService.dismiss();
    this.scheduleSuccessBannerHide();
  }

  /**
   * Mirrors free-reg 4c payment.page afterFourCData — shared offer/purchase capping handling.
   */
  private afterFourCData(r: Data): void {
    if (!r) {
      this.authService.errorHandlerLogs("afterFourCData received null response from check4cFlow");
      this.isCappingCheckDone = true;
      return;
    }

    if (r?.offerCapping) {
      const isOfferCapped =
        r.offerCapping?.cappingStatus === true ||
        r.offerCapping?.capping_status === true;

      // Remember capped state — toast is shown only when user clicks Play Now
      if (isOfferCapped) {
        this.isOfferCapped = true;
        // The paid base journey is independent of this optional offer's cap.
        if (!this.confirmedBaseJourney()) this.paymentNavigationService.handleNavigationForOfferCapping(r);
        this.isCappingCheckDone = false;
      }
      if (r?.offerCapping?.offerId) {
        this.offerId = r.offerCapping.offerId;
      }
      this.processcustomer_offer = r?.offerCapping?.alternateOfferId;
    }

    if (!r?.purchaseCapping?.goodForPayment) {
      if (r?.purchaseCapping?.paymentCappingSyndicateId) {
        if (!this.confirmedBaseJourney()) this.paymentNavigationService.handlePurchaseCapping(r, this.userId);
      }
    } else {
      this.isCappingCheckDone = true;
    }
  }

  /**
   * Toast 1165 — same pattern as store offer capping (always falls back if constants missing).
   */
  private async showOfferCappingBlockedToast(): Promise<void> {
    const defaultMsg = {
      message: {
        line_1: "D-1165-This offer is no longer available because you've reached the purchase limit.",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      },
    };

    try {
      const translatedMsg = await this.authService.getConstantsMsg("1165");
      this.authService.showToastMessage(translatedMsg, defaultMsg);
    } catch (error) {
      this.authService.errorHandlerLogs("[OfferCapping] Error fetching offer capping toast message", error);
      this.authService.showToastMessage({}, defaultMsg);
    }
  }

  private scheduleSuccessBannerHide(): void {
    if (this.successBannerScheduleTimer) {
      clearTimeout(this.successBannerScheduleTimer);
      this.successBannerScheduleTimer = null;
    }
    if (this.successBannerHideTimer) {
      clearTimeout(this.successBannerHideTimer);
      this.successBannerHideTimer = null;
    }

    this.successBannerScheduleTimer = setTimeout(() => {
      this.successBannerScheduleTimer = null;
      if (this.isDestroyed) {
        return;
      }
      this.successBannerHideTimer = scheduleSuccessBannerHideTimer(
        this.dynamicContent?.nativeElement,
        this.paymentPageData
      );
    }, 0);
  }

  replaceExpiryDate(template: string): SafeHtml {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const lastDayNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0);

    const formattedDate = this.formatDate(lastDayNextMonth);
    const updatedTemplate = template.replace('[dv_month_expiry]', formattedDate);

    // Important: Sanitize the HTML to prevent XSS vulnerabilities
    return this.sanitizer.bypassSecurityTrustHtml(updatedTemplate);
}
  formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  replacePlaceholderWithExpiry(template: string): string {
    const expiryDate = this.getNextMonthLastDate();
    return template.replace('[dv_month_expiry]', expiryDate);
  }

  getNextMonthLastDate(): string {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0); // Last day of next month
    const day = String(nextMonth.getDate()).padStart(2, '0');
    const month = String(nextMonth.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = nextMonth.getFullYear();
    return `${day}/${month}/${year}`;
  }

  ionViewDidEnter() {
    this.startAutomaticBaseClaim();
    const epoch = this.claimNavigationEpoch;
    setTimeout(() => {
      if (this.isDestroyed || epoch !== this.claimNavigationEpoch) return;
      const anchorElement = this.dynamicContent?.nativeElement?.querySelector(".claim_btn");
      if (anchorElement) {
        this.renderer.listen(anchorElement, "click", () => {
          if(this.iframeSmsJourneyDoc && this.iframeSmsJourneyDoc !== "") {
            this.handleIframeCloseJourney();
          } else if(this.altProsubDetails?.iframe_close_postapi) {
            this.iframePostMessageCheck();
          } else {
            this.openUrl();
          }
        });
      }

      const playButton = this.dynamicContent?.nativeElement?.querySelector(".play-btn");
      if (playButton) {
        this.renderer.listen(playButton, "click", () => {
          this.redirectToAction();
        });
      }

      const noThanksButton = this.dynamicContent?.nativeElement?.querySelector(".not-now-btn");
      if (noThanksButton) {
        this.renderer.listen(noThanksButton, "click", () => {
          this.openNoThanks();
        });
      }
    }, 3000);
  }

  private confirmedBaseJourney() {
    const journey = this.paymentService.confirmedPaidJourney;
    const user = getAuth().currentUser;
    return journey && user && !user.isAnonymous && journey.userId === user.uid &&
      (String(journey.nextProsubId || "") === String(this.prosubId) ||
        (String(journey.prosubId) === String(this.prosubId) && this.optionalPurchaseConfirmedContext === journey)) ? journey : null;
  }

  private startAutomaticBaseClaim(): void {
    if (!this.confirmedBaseJourney()) return;
    if (!this.automaticBaseClaimProfileSubscription) {
      this.automaticBaseClaimProfileSubscription = this.paymentNavigationService.profileCompletion$().subscribe(() => {
        if (this.automaticBaseClaimTimer != null) clearTimeout(this.automaticBaseClaimTimer);
        this.automaticBaseClaimTimer = null;
        void this.continueAutomaticBaseClaim();
      });
    }
    void this.continueAutomaticBaseClaim();
  }

  /** Show the already-paid base result over this offer. Never invoke optional Play. */
  private async continueAutomaticBaseClaim(): Promise<void> {
    const journey = this.confirmedBaseJourney();
    if (!journey || !this.viewActive || this.isDestroyed || this.automaticBaseClaimInProgress ||
        this.automaticBaseClaimCompleted === journey) return;
    if (!window.navigator.onLine) {
      this.scheduleAutomaticBaseClaim();
      return;
    }
    const epoch = this.claimNavigationEpoch;
    const isCurrent = () => !this.isDestroyed && this.viewActive &&
      epoch === this.claimNavigationEpoch && this.confirmedBaseJourney() === journey;
    this.automaticBaseClaimInProgress = true;
    this.automaticBaseClaimPending = true;
    try {
      localStorage.setItem("flowPath", "free-reg-step4b");
      await this.paymentNavigationService.callInt2({
        Prosub_id: this.prosubId, TP1: this.tp1, Tp2: this.tp2, TP3: this.tp3, T_ID: this.tID,
        upsellID: this.upsellID, timerOn: false, spinnerAnimation: false, isPaymentDone: true,
        addingCard: false, addNewCardInProgress: false, _syndicateId: this.syndicateId,
        isCurrent, getCheckCardDetailId: () => ({ id: this.purchaseID }), onStateChange: () => {},
      }, this.optionalPurchaseConfirmedContext === journey && this.addOnProsubId ? {} : { claimFallback: true });
      if (isCurrent()) {
        this.automaticBaseClaimCompleted = journey;
        this.automaticBaseClaimPending = false;
      }
    } catch (error) {
      if (!isCurrent()) return;
      this.authService.errorHandlerLogs("4B base result is preparing", error);
      this.scheduleAutomaticBaseClaim();
    } finally {
      if (isCurrent()) this.automaticBaseClaimInProgress = false;
    }
  }

  private scheduleAutomaticBaseClaim(): void {
    if (this.isDestroyed || !this.viewActive || this.automaticBaseClaimTimer != null) return;
    const delay = ++this.automaticBaseClaimAttempts <= 2 ? 2000 : 30000;
    this.automaticBaseClaimTimer = setTimeout(() => {
      this.automaticBaseClaimTimer = null;
      void this.continueAutomaticBaseClaim();
    }, delay);
  }

  private async resolveClaimDestination(addonProsubId: string): Promise<string | null> {
    // An empty ID makes PaymentDataService reread the current offer. Terminal
    // Claim must continue the existing purchase instead of generating a blank-ID URL.
    if (!addonProsubId || addonProsubId === String(this.prosubId)) return null;
    try {
      const addon = await firstValueFrom(this.paymentDataService.getPaymentPageData(addonProsubId).pipe(
        take(1),
        timeout({ first: PaymentService.POST_PAYMENT_NEXT_STEP_TIMEOUT_MS }),
        defaultIfEmpty(null),
      ));
      const page = typeof addon?.optional_page === "string" ? addon.optional_page.trim() : "";
      return page ? `${page}?prosub_id=${encodeURIComponent(addonProsubId)}` : null;
    } catch (error) {
      this.authService.errorHandlerLogs("4B Claim next offer unavailable", error);
      return null;
    }
  }

  private async navigateClaimDestination(link: string): Promise<boolean> {
    let deadline: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        this.router.navigateByUrl(link),
        new Promise<boolean>(resolve => {
          deadline = setTimeout(() => resolve(false), PaymentService.POST_PAYMENT_NEXT_STEP_TIMEOUT_MS);
        }),
      ]);
    } finally {
      if (deadline !== undefined) clearTimeout(deadline);
    }
  }

  async openUrl(): Promise<void> {
    if (this.isDestroyed || this.claimNavigationInProgress || this.automaticBaseClaimInProgress) return;
    this.claimNavigationInProgress = true;
    const epoch = this.claimNavigationEpoch;
    const isCurrent = () => !this.isDestroyed && epoch === this.claimNavigationEpoch;
    this.loadingService.present();
    try {
      const link = await this.claimDestination;
      if (!isCurrent()) return;
      if (link) {
        try {
          if (await this.navigateClaimDestination(link)) return;
        } catch (error) {
          if (!isCurrent()) return;
          this.authService.errorHandlerLogs("4B Claim next offer navigation failed", error);
        }
      }
      if (!isCurrent()) return;
      // Claim only continues an existing result. Play Now retains the separate
      // explicit purchase action; no payment request is started here.
      await this.paymentNavigationService.callInt2({
        Prosub_id: this.prosubId, TP1: this.tp1, Tp2: this.tp2, TP3: this.tp3, T_ID: this.tID,
        upsellID: this.upsellID, timerOn: false, spinnerAnimation: false, isPaymentDone: true,
        addingCard: false, addNewCardInProgress: false, _syndicateId: this.syndicateId,
        isCurrent, getCheckCardDetailId: () => ({ id: this.purchaseID }),
        onStateChange: () => {},
      }, { claimFallback: true });
    } catch (error) {
      if (isCurrent()) this.authService.errorHandlerLogs("4B Claim continuation failed", error);
    } finally {
      if (isCurrent()) {
        this.claimNavigationInProgress = false;
        this.loadingService.dismiss();
      }
    }
  }

  /**
   * Call interstitial 2 functionalities and redirect to specific page
   */
  async redirectToAction() {
    this.resumeOptionalPurchaseStatus();
    const user = getAuth().currentUser;
    if (!user || user.isAnonymous || this.optionalPurchaseInProgress ||
        (this.optionalPurchaseConfirmedContext && this.paymentService.confirmedPaidJourney === this.optionalPurchaseConfirmedContext)) return;
    const userId = user.uid;
    const prosubId = this.prosubId;
    const epoch = this.claimNavigationEpoch;
    const isCurrent = () => !this.isDestroyed && this.viewActive && epoch === this.claimNavigationEpoch &&
      this.prosubId === prosubId && getAuth().currentUser?.uid === userId && !getAuth().currentUser?.isAnonymous;
    if (!isCurrent()) return;
    this.optionalPurchaseInProgress = true;
    try {
      await this.purchaseOptionalOffer(userId, prosubId, isCurrent);
    } finally {
      this.optionalPurchaseRequestInFlight = false;
      // An uncertain provider response is still the original payment attempt.
      // Only an authoritative failed purchase may enable a fresh Play action.
      this.optionalPurchaseInProgress = !!this.pendingOptionalPurchase;
      this.resumeOptionalPurchaseStatus();
    }
  }

  private async purchaseOptionalOffer(userId: string, prosubId: string, isCurrent: () => boolean): Promise<void> {
    // Firebase users/{uid}/offer_capping/{offerId}.capping_status — toast only when true
    const purchaseForCapCheck = {
      ...this.purchaseDetail,
      offer_id: this.offerId || this.purchaseDetail?.offer_id,
    };
    const isFirebaseOfferCapped = await this.storeService.validateOfferCappingBeforePurchase(
      purchaseForCapCheck,
      "free_reg_step4b_play"
    );
    if (!isCurrent() || isFirebaseOfferCapped) {
      return;
    }

    // Backup: check4cFlow reported cappingStatus / capping_status true
    if (this.isOfferCapped) {
      await this.showOfferCappingBlockedToast();
      return;
    }

    // Same readiness gate as free-reg 4c (`canUseStoredCard` uses isCappingCheckDone)
    if (!this.isCappingCheckDone) {
      return;
    }

    let submittedPurchase: PendingOptionalPurchase | null = null;
    let submissionNotInvoked = false;
    try {
      this.loadingService.present();

      this.purchaseDetail.prize = 0;

      const storedCardInfo = await firstValueFrom(
        this.walletService.storedCards$.pipe(
          map((storedCards) => {
            return !this.purchaseID || this.purchaseID === "null" || this.purchaseID === null
              ? storedCards[0]
              : storedCards.find((card) => card.encrypt_card_id === this.purchaseID) || storedCards[0]; 
          }),
          retry({ count: 10, delay: 5000 })
        )
      );

      if (!isCurrent()) return;
      if (!storedCardInfo) {
        this.loadingService.dismiss();
        await this.showToast("1049", "Transactions failed, please add new card");
        return
      }

      const result = await firstValueFrom(this.paymentService
        .insertCustomerPurchase(
          { ...this.purchaseDetail, ...storedCardInfo },
          "insert_customer_purchase_4c_payment"
        ));

      if (!isCurrent()) return;
      if (result?.status === "rejected") {
        this.loadingService.dismiss();
        await this.showToast(result?.error_code);
        return;
      }

      if (result?.response && result?.response?.status === CommonVariable.success) {
        if (this.altProsubDetails?.free_reg_mod_id === CommonVariable.prizeDrawToastCheck) {
          this.paymentService?.showPrizeDrawToastMessage();
        }
        const insertCustomerPurchaseRes = result;
        const customerPurchaseId = result?.response?.customer_purchase_id || result?.response?.encrypted_customer_purchase_id;

        const offerForCappingCheck = this.processcustomer_offer || this.offerId;
        const resCustomer = await firstValueFrom(this.paymentService
          .processcustomer_purchase(
            insertCustomerPurchaseRes?.response?.customer_purchase_id,
            prosubId,
            offerForCappingCheck
          )
        );

        if (!isCurrent()) return;
        if (resCustomer?.status === "rejected") {
          this.loadingService.dismiss();
          await this.showToast(resCustomer?.error_code);
          return;
        }

        if (resCustomer.status == 0) {
          const alt_prosub_id = resCustomer.Alternate_prosub_id;
          if (!alt_prosub_id) {
            this.router.navigate([
              `/free-reg-step4c/?prosub_id=${alt_prosub_id}&TP1=${this.tp1}&TP2=${this.tp2}&TP3=${this.tp3}&T_ID=${this.tID}`,
            ]);
          } else {
            this.router.navigateByUrl("product-not-available");
          }
        } else if (resCustomer.status == 2) {
          this.router.navigate([
            `/free-reg-complete-profile/?prosub_id=${this.prosubId}&TP1=${this.tp1}&TP2=${this.tp2}&TP3=${this.tp3}&T_ID=${this.tID}&sid=${resCustomer.encrypt_syndicate_id}`,
          ]);
        } else if (resCustomer.status == 1) {

          const pending: PendingOptionalPurchase = {
            userId, prosubId, receiptId: String(customerPurchaseId || ""),
            customerPurchaseId: String(insertCustomerPurchaseRes.response.encrypted_customer_purchase_id || customerPurchaseId || ""),
          };
          if (!pending.customerPurchaseId) return;
          submittedPurchase = pending;
          this.pendingOptionalPurchase = pending;
          this.optionalPurchaseRequestInFlight = true;
          this.optionalPurchaseStatusAttempts = 0;
          try {
            sessionStorage.setItem(this.optionalPurchaseStorageKey(userId, prosubId), JSON.stringify(pending));
          } catch { /* The in-memory payment lock still protects this attempt. */ }
          // Persist the original purchase before submitting. A timeout or lost
          // response can resume status reads, but can never replay this request.
          const processPayResult: any = await firstValueFrom(this.paymentService
            .processCardPayment(
              insertCustomerPurchaseRes.response.customer_purchase_id,
              storedCardInfo,
              "/api_post_request/process_4c_payment_existing_card",
              isCurrent,
              () => {
                submissionNotInvoked = true;
                this.releaseUninvokedOptionalPurchase(pending);
              },
            ).pipe(take(1), timeout({ first: 20000 })));
          this.optionalPurchaseRequestInFlight = false;
          if (!isCurrent()) return;
          if (processPayResult?.response?.status === CommonVariable.success) {
            await this.confirmOptionalPurchase(pending, processPayResult.response);
          } else {
            this.resumeOptionalPurchaseStatus();
          }
        }

      } else {
        await this.showToast("1051", "Failed in make payment request");
      }
    } catch (error) {
      if ((submissionNotInvoked || (error as { code?: unknown })?.code === "CALLABLE_NOT_INVOKED") && submittedPurchase) {
        // The transport proved that it never invoked the charge. Clear only this
        // attempt; a lost/unknown provider response must keep its original lock.
        this.releaseUninvokedOptionalPurchase(submittedPurchase);
        if (isCurrent()) this.loadingService.dismiss();
        return;
      }
      if (this.pendingOptionalPurchase) {
        this.optionalPurchaseRequestInFlight = false;
        this.authService.errorHandlerLogs("4B original payment confirmation pending", error);
        this.resumeOptionalPurchaseStatus();
      } else if (isCurrent()) {
        await this.showToast("1051", "Failed in make payment request");
      }
    }
  }

  private releaseUninvokedOptionalPurchase(pending: PendingOptionalPurchase): void {
    try {
      const key = this.optionalPurchaseStorageKey(pending.userId, pending.prosubId);
      const saved = JSON.parse(sessionStorage.getItem(key) || "null");
      if (saved?.userId === pending.userId && saved?.prosubId === pending.prosubId &&
          saved?.customerPurchaseId === pending.customerPurchaseId && saved?.receiptId === pending.receiptId) {
        sessionStorage.removeItem(key);
      }
    } catch { /* A newer or malformed pointer cannot establish a payment. */ }
    if (this.pendingOptionalPurchase !== pending) return;
    this.suspendOptionalPurchaseStatus();
    this.pendingOptionalPurchase = null;
    this.optionalPurchaseRequestInFlight = false;
    this.optionalPurchaseInProgress = false;
  }

  private optionalPurchaseStorageKey(userId: string, prosubId: string): string {
    return `pending4BPurchase:v1:${encodeURIComponent(userId)}:${encodeURIComponent(prosubId)}`;
  }

  private ownsOptionalPurchase(pending: PendingOptionalPurchase): boolean {
    const user = getAuth().currentUser;
    return !this.isDestroyed && this.viewActive && !!user && !user.isAnonymous &&
      user.uid === pending.userId && String(this.prosubId) === pending.prosubId &&
      this.pendingOptionalPurchase === pending;
  }

  private suspendOptionalPurchaseStatus(): void {
    this.optionalPurchaseStatusRead?.unsubscribe();
    this.optionalPurchaseStatusRead = undefined;
    if (this.optionalPurchaseStatusTimer != null) clearTimeout(this.optionalPurchaseStatusTimer);
    this.optionalPurchaseStatusTimer = undefined;
  }

  private resumeOptionalPurchaseStatus(): void {
    const user = getAuth().currentUser;
    if (this.isDestroyed || !this.viewActive || !user || user.isAnonymous || !this.prosubId) return;
    if (!this.pendingOptionalPurchase) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.optionalPurchaseStorageKey(user.uid, this.prosubId)) || "null");
        if (saved?.userId === user.uid && saved?.prosubId === this.prosubId &&
            typeof saved.customerPurchaseId === "string" && saved.customerPurchaseId.length > 0 &&
            saved.customerPurchaseId.length <= 2048 && !/[\/\r\n]/.test(saved.customerPurchaseId) &&
            typeof saved.receiptId === "string") this.pendingOptionalPurchase = saved;
      } catch { /* Browser storage supplies a pointer, never payment confirmation. */ }
    }
    const pending = this.pendingOptionalPurchase;
    if (!pending || !this.ownsOptionalPurchase(pending)) return;
    this.optionalPurchaseInProgress = true;
    if (this.optionalPurchaseRequestInFlight) return;
    if (this.optionalPurchaseConfirmedContext &&
        this.optionalPurchaseConfirmedContext === this.paymentService.confirmedPaidJourney) return;
    if (this.optionalPurchaseStatusRead || this.optionalPurchaseStatusTimer != null) return;
    const epoch = this.claimNavigationEpoch;
    const isCurrent = () => epoch === this.claimNavigationEpoch && this.ownsOptionalPurchase(pending);
    const deferRead = () => {
      if (!isCurrent() || (this.optionalPurchaseConfirmedContext &&
          this.optionalPurchaseConfirmedContext === this.paymentService.confirmedPaidJourney)) return;
      this.suspendOptionalPurchaseStatus();
      const delay = Math.min(30000, 2000 * Math.pow(2, Math.min(this.optionalPurchaseStatusAttempts++ || 0, 4)));
      this.optionalPurchaseStatusTimer = setTimeout(() => {
        this.optionalPurchaseStatusTimer = undefined;
        if (isCurrent()) this.resumeOptionalPurchaseStatus();
      }, delay);
    };
    if (!window.navigator.onLine) { deferRead(); return; }
    // Assign before subscribing: cached snapshots may complete synchronously.
    const read = new Subscription();
    this.optionalPurchaseStatusRead = read;
    try {
      read.add(this.paymentService.watchCustomerPurchaseSnapshot(pending.customerPurchaseId)
        .pipe(timeout({ each: 20000 }))
        .subscribe({
          next: status => {
            if (!isCurrent()) return;
            if (Number(status?.processed_status) === 2) {
              this.suspendOptionalPurchaseStatus();
              void this.confirmOptionalPurchase(pending, status?.api_response?.response || status).catch(error => {
                this.authService.errorHandlerLogs("4B confirmed purchase continuation unavailable", error);
              });
            } else if (Number(status?.processed_status) === -1) {
              this.suspendOptionalPurchaseStatus();
              try { sessionStorage.removeItem(this.optionalPurchaseStorageKey(pending.userId, pending.prosubId)); }
              catch { /* No payment status is inferred from storage. */ }
              this.pendingOptionalPurchase = null;
              this.optionalPurchaseInProgress = false;
              this.loadingService.dismiss();
              void this.showToast("1049", "Transactions failed, please add new card").catch(error => {
                this.authService.errorHandlerLogs("4B payment failure feedback unavailable", error);
              });
            }
          },
          error: deferRead,
          complete: deferRead,
        }));
    } catch (error) {
      this.authService.errorHandlerLogs("4B original purchase status unavailable", error);
      deferRead();
    }
  }

  private async confirmOptionalPurchase(pending: PendingOptionalPurchase, response: Data): Promise<void> {
    if (!this.ownsOptionalPurchase(pending) || (this.optionalPurchaseConfirmedContext &&
        this.optionalPurchaseConfirmedContext === this.paymentService.confirmedPaidJourney)) return;
    this.suspendOptionalPurchaseStatus();
    this.paymentService.recordConfirmedPaidJourney({ ...pending });
    this.optionalPurchaseConfirmedContext = this.paymentService.confirmedPaidJourney;
    this.paymentService.resetPostPaymentTicketState();
    this.paymentService.salesJourneyCustomerPurchaseId$.next(pending.customerPurchaseId);
    this.paymentService.salesJournyPaymentDetails$.next(response?.syndicate_id || response?.encrypt_syndicate_id || null);
    try { this.paymentService.setSyndicatePending(); }
    catch (error) { this.authService.errorHandlerLogs("4B syndicate pending update unavailable", error); }
    try {
      this.gtmSegment.userProduct$.next(this.gtmSegment.userProduct$.value);
      this.gtmSegment.trackHopePaymentConfirmed(this.altProsubDetails?.prosub_id || this.prosubId);
      this.gtmSegment.GTM_CJ_conversion(
        this.altProsubDetails?.price,
        this.purchaseDetail?.offers?.currency || "GBP",
        this.altProsubDetails?.offer_id,
        this.gtmSegment?.userIsNew ? "new" : "return",
        CommonVariable.countryCode,
        this.purchaseDetail?.p_type || "10",
        this.altProsubDetails?.prosub_id,
        pending.receiptId,
      );
    } catch (error) { this.authService.errorHandlerLogs("4B paid analytics unavailable", error); }
    await this.callInt2();
  }

  async callInt2() {
    // A confirmed optional purchase shares the same fresh profile/tickets gate.
    // Presentation retries never re-enter redirectToAction or another charge.
    await this.continueAutomaticBaseClaim();
  }

  fetchSyndicate() {
    this.paymentService?.fetchSyndicateAfterPayment(this.syndicateId, (syndicateInfo) => {
      this.showTicket(syndicateInfo);
    });
  }

  async showTicket(syndicateInfo) {
    const ticketsModule = await import(
      "../../syndicate/syndicate-tickets/syndicate-tickets.component"
    ).catch((err) => {
      this.authService.errorHandlerLogs("showTicket: failed to load syndicate-tickets chunk", err);
      return null;
    });
    const SyndicateTicketsComponent = ticketsModule?.SyndicateTicketsComponent;
    if (!SyndicateTicketsComponent) {
      this.authService.errorHandlerLogs("showTicket: SyndicateTicketsComponent unavailable", null);
      await this.showToast("1014", "Something went wrong!");
      return;
    }

    const syndicateTickets = await this.modalController.create({
      component: SyndicateTicketsComponent,
      componentProps: { syndicate: syndicateInfo },
      cssClass: "modal-fullScreen",
    });

    return await syndicateTickets.present();
  }

  /**
   * Open no thanks popup
   */
  openNoThanks() {
    this.matDialog.open(NoThanksPopupComponent, {
      height: "40%",
      width: "80%",
    });
  }

  /**
   * Show toast message with translated message
   * @param constantsMsg constants message key
   * @param defaultMessageString default message string
   */
  async showToast(constantsMsg: string, defaultMessageString: string = null): Promise<void> {
    const translatedMsg = await this.authService.getConstantsMsg(constantsMsg);
    if (translatedMsg) {
      const defaultMsg = {
        message: {
          line_1: defaultMessageString || "Something went wrong",
          line_2: "",
        },
        style: {
          icon: "tick circle error.svg",
          type: CommonVariable.error,
        },
      };
      return this.authService.showToastMessage(translatedMsg, defaultMsg);
    }
    throw new Error("There is no translated message found");
  }

  private handleIframeCloseJourney() {
    const userId = this.tokenService.getUserId();
    if (userId) {
      const iframeClosePayload = {
        userId,
        sms_message_doc_id: this.iframeSmsJourneyDoc,
      };

      this.authService
        .getV2CloudFunction("sendsmspartner", iframeClosePayload)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.iframePostMessageCheck();
          },
          error: (err) => {
            this.iframePostMessageCheck();
            return err;
          },
        });
    }
  }

  // To check if the iframe close post message node is enabled for given prosub and send post message to parent window
  private iframePostMessageCheck(): void {
    if (this.altProsubDetails?.iframe_close_postapi) {
      this.authPartialRegService.closeIframePostMessage();
    }
  }

}
